import { type FormEvent, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Icons } from "@/components/ui/icons";
import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { cn } from "@/lib/cn";

/**
 * Workshop: Input bar — unified glass pill with agent stack + text input + send.
 *
 * Agent avatars are stacked (overlapping) by default. On hover they fan out
 * to reveal each agent for selection. Selected agent moves to front of stack
 * and gets an accent ring. A count badge shows total agents when collapsed.
 */

interface InputAgent {
  id: string;
  name: string;
  color?: string;
  avatar_seed?: string;
}

interface InputBarProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  agents?: InputAgent[];
  onAddAgent?: () => void;
  /** Controlled selected agent — parent drives selection */
  selectedAgentId?: string | null;
  onSelectAgent?: (agentId: string | null) => void;
  /** For workshop showcase: open the mention popup on mount */
  initialMentionOpen?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

/* ---------- Agent avatar (inline, small) ---------- */

function AgentChip({
  agent,
  selected,
  onClick,
  style,
  testId,
}: {
  agent: InputAgent;
  selected: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-200",
        selected && "ring-1 ring-accent/80 shadow-glow-accent-sm",
      )}
      style={style}
      aria-label={`Select ${agent.name}`}
      data-testid={testId}
    >
      <AgentAvatarPreview
        styleKey="glass"
        seed={agent.avatar_seed ?? agent.name}
        size={24}
        sizeClassName="h-6 w-6"
      />
    </button>
  );
}

/* ---------- Agent stack ---------- */

/**
 * Renders agents in a tight overlapping stack. On hover, fans out.
 * On select, the chosen agent shuffles to the front with a smooth
 * translate animation (card-shuffle effect). We keep agents in their
 * original order in the DOM and use translateX to position them,
 * so React doesn't remount and the transition animates smoothly.
 */
function AgentStack({
  agents,
  selectedId,
  onSelect,
  onAddAgent,
  testId,
}: {
  agents: InputAgent[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAddAgent?: () => void;
  testId?: string;
}) {
  const [hovered, setHovered] = useState(false);

  if (agents.length === 0) {
    return (
      <button
        type="button"
        onClick={onAddAgent}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-glass-stroke-light text-muted transition-colors hover:text-primary"
        aria-label="Add agent"
        data-testid={testId ? `${testId}-add` : undefined}
      >
        <Icons.Plus className="h-3 w-3" />
      </button>
    );
  }

  // Collapsed overlap: each subsequent avatar shifts left by this much
  const COLLAPSED_OVERLAP = -16; // heavy overlap, only ~8px crescent visible
  const EXPANDED_GAP = 4;
  const CHIP_SIZE = 24; // h-6 = 24px

  // Compute visual order: selected agent gets position 0, others shift right
  // But keep DOM order stable so React animates via transform, not remount
  const selectedIndex = selectedId
    ? agents.findIndex((a) => a.id === selectedId)
    : -1;

  function getTranslateX(domIndex: number): number {
    if (hovered) {
      // Fanned out: evenly spaced
      return domIndex * (CHIP_SIZE + EXPANDED_GAP);
    }

    if (selectedIndex === -1) {
      // No selection: simple stack
      return domIndex * (CHIP_SIZE + COLLAPSED_OVERLAP);
    }

    // Card shuffle: selected slides to pos 0, others shift right
    if (domIndex === selectedIndex) {
      return 0; // selected goes to front
    }
    // Count how many agents are visually before this one
    const visualPos =
      domIndex < selectedIndex ? domIndex + 1 : domIndex;
    return visualPos * (CHIP_SIZE + COLLAPSED_OVERLAP);
  }

  function getZIndex(domIndex: number): number {
    if (hovered) return agents.length - domIndex;
    if (domIndex === selectedIndex) return agents.length + 1;
    if (selectedIndex === -1) return agents.length - domIndex;
    const visualPos =
      domIndex < selectedIndex ? domIndex + 1 : domIndex;
    return agents.length - visualPos;
  }

  // Total width of the stack for the container
  const totalWidth = hovered
    ? agents.length * CHIP_SIZE + (agents.length - 1) * EXPANDED_GAP + CHIP_SIZE + EXPANDED_GAP // +1 for add button
    : agents.length * CHIP_SIZE + (agents.length - 1) * COLLAPSED_OVERLAP + CHIP_SIZE + COLLAPSED_OVERLAP; // +1 for add button

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={testId}
    >
      <div
        className="relative flex items-center"
        style={{
          width: Math.max(totalWidth, CHIP_SIZE),
          height: CHIP_SIZE,
          transition: "width 200ms ease-out",
        }}
      >
        {agents.map((agent, i) => (
          <div
            key={agent.id}
            className="absolute left-0 top-0"
            style={{
              transform: `translateX(${getTranslateX(i)}px)`,
              zIndex: getZIndex(i),
              transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <AgentChip
              agent={agent}
              selected={agent.id === selectedId}
              onClick={() =>
                onSelect(agent.id === selectedId ? null : agent.id)
              }
              testId={testId ? `${testId}-${agent.id}` : undefined}
            />
          </div>
        ))}

        {/* Add agent button */}
        <div
          className="absolute left-0 top-0"
          style={{
            transform: `translateX(${
              hovered
                ? agents.length * (CHIP_SIZE + EXPANDED_GAP)
                : agents.length * (CHIP_SIZE + COLLAPSED_OVERLAP)
            }px)`,
            zIndex: 0,
            transition: "transform 250ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <button
            type="button"
            onClick={onAddAgent}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-glass-stroke-light text-muted transition-colors hover:text-primary"
            aria-label="Add agent"
            data-testid={testId ? `${testId}-add` : undefined}
          >
            <Icons.Plus className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Count badge — pinned to top-right of the frontmost avatar */}
      <span
        className={cn(
          "pointer-events-none absolute flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-accent px-0.5 text-[8px] font-bold text-white shadow-sm transition-opacity duration-150",
          hovered ? "opacity-0" : "opacity-100",
        )}
        style={{
          top: -4,
          left: CHIP_SIZE - 4,
          zIndex: agents.length + 2,
        }}
        aria-label={`${agents.length} agents`}
      >
        {agents.length}
      </span>
    </div>
  );
}

/* ---------- Main component ---------- */

export function InputBar({
  value,
  onChange,
  onSubmit,
  agents = [],
  onAddAgent,
  selectedAgentId: controlledSelectedId,
  onSelectAgent,
  initialMentionOpen = false,
  placeholder = "Ask anything...",
  disabled = false,
  className,
  "data-testid": testId = "input-bar",
}: InputBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [mentionOpen, setMentionOpen] = useState(initialMentionOpen);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);

  // Support both controlled and uncontrolled selection
  const selectedAgentId = controlledSelectedId !== undefined ? controlledSelectedId : internalSelectedId;
  const setSelectedAgentId = onSelectAgent ?? setInternalSelectedId;

  const selectedAgent = agents.find((a) => a.id === selectedAgentId);
  const dynamicPlaceholder = selectedAgent
    ? `Ask ${selectedAgent.name}...`
    : placeholder;

  const filteredAgents = agents.filter((a) =>
    a.name.toLowerCase().startsWith(mentionQuery.toLowerCase()),
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    onChange(newValue);

    const cursor = e.target.selectionStart ?? newValue.length;
    const before = newValue.slice(0, cursor);
    const atIdx = before.lastIndexOf("@");

    if (atIdx !== -1) {
      const query = before.slice(atIdx + 1);
      if (!query.includes(" ")) {
        setMentionQuery(query);
        setMentionOpen(true);
        setMentionIndex(0);
        return;
      }
    }

    setMentionOpen(false);
    setMentionQuery("");
  }

  function selectMentionAgent(agent: InputAgent) {
    const atIdx = value.lastIndexOf("@" + mentionQuery);
    if (atIdx !== -1) {
      const before = value.slice(0, atIdx);
      const after = value.slice(atIdx + mentionQuery.length + 1);
      onChange(before + "@" + agent.name + " " + after);
    }
    // Select the mentioned agent — triggers card-shuffle animation
    setSelectedAgentId(agent.id);
    setMentionOpen(false);
    setMentionQuery("");
    inputRef.current?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (mentionOpen && filteredAgents.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => Math.min(i + 1, filteredAgents.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (filteredAgents[mentionIndex]) selectMentionAgent(filteredAgents[mentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        setMentionOpen(false);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!value.trim() || disabled) return;
      onSubmit();
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!value.trim() || disabled) return;
    onSubmit();
  }

  const canSubmit = value.trim().length > 0 && !disabled;

  return (
    <div className={cn("relative", className)} data-testid={testId}>
      {/* Agent mention popup */}
      {mentionOpen && filteredAgents.length > 0 && (
        <div
          className="absolute bottom-full left-0 mb-1.5 w-48 overflow-hidden rounded-xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm"
          data-testid="mention-popup"
        >
          <div className="border-b border-glass-stroke-subtle px-2.5 py-1.5">
            <span className="text-[10px] font-medium text-muted">Mention an agent</span>
          </div>
          <div className="p-1">
            {filteredAgents.map((agent, i) => (
              <button
                key={agent.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectMentionAgent(agent);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors",
                  i === mentionIndex ? "bg-glass-stroke-light" : "hover:bg-glass-stroke-subtle",
                )}
                data-testid={`mention-agent-${agent.id}`}
              >
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[8px] font-semibold text-white"
                  style={{ backgroundColor: agent.color ?? "#818CF8" }}
                >
                  {agent.name[0]?.toUpperCase()}
                </span>
                <span className="text-[11px] font-medium text-primary">{agent.name}</span>
                {i === mentionIndex && (
                  <Icons.ChevronRight className="ml-auto h-3 w-3 text-muted" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Unified glass pill: agents + input + send */}
      <form
        onSubmit={handleSubmit}
        className="flex h-10 items-center gap-2 rounded-full border border-glass-stroke bg-glass-fill p-1.5 shadow-glass backdrop-blur-glass"
      >
        {/* Agent stack */}
        <AgentStack
          agents={agents}
          selectedId={selectedAgentId}
          onSelect={setSelectedAgentId}
          onAddAgent={onAddAgent}
          testId="input-agents"
        />

        {/* Subtle divider */}
        {agents.length > 0 && (
          <div className="h-4 w-px shrink-0 bg-glass-stroke-subtle" aria-hidden="true" />
        )}

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={dynamicPlaceholder}
          disabled={disabled}
          className="h-full flex-1 bg-transparent text-[13px] text-primary outline-none placeholder:text-muted disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="input-bar-field"
        />

        {/* Send button — concentric circle */}
        <button
          type="submit"
          disabled={!canSubmit}
          aria-label="Send"
          data-testid="input-bar-send"
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
            canSubmit
              ? "bg-accent text-white hover:opacity-90"
              : "cursor-not-allowed bg-transparent text-muted",
          )}
        >
          <Icons.ArrowUp className="h-3.5 w-3.5" />
        </button>
      </form>
    </div>
  );
}

export type { InputBarProps, InputAgent };
